import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../components/Form/controls/FormTextField.jsx";
import FormSwitch from "../../../components/Form/controls/FormSwitch.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import {
  createConnector,
  fetchConnector,
  updateConnector,
} from "@accredo/fe-shared/services/importConnectorService.js";

const schema = yup.object({
  name: yup.string().required("Name ist erforderlich"),
  slug: yup.string().required("Slug ist erforderlich"),
  description: yup.string().nullable(),
  is_active: yup.boolean(),
});

const DEFAULT_VALUES = {
  name: "",
  slug: "",
  description: "",
  is_active: true,
};

function ImportConnectorFormModal({ open, onClose, connectorId }) {
  const isEdit = Boolean(connectorId);
  const { enqueueSnackbar } = useSnackbar();

  const [defaultValues, setDefaultValues] = useState(DEFAULT_VALUES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setDefaultValues(DEFAULT_VALUES);
      return;
    }

    setLoading(true);
    fetchConnector(connectorId)
      .then((res) => {
        const c = res.data;
        setDefaultValues({
          name: c.name ?? "",
          slug: c.slug ?? "",
          description: c.description ?? "",
          is_active: c.is_active ?? true,
        });
      })
      .catch(() =>
        enqueueSnackbar("Connector konnte nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [open, connectorId, isEdit, enqueueSnackbar]);

  async function handleSubmit(data, { setError }) {
    try {
      const payload = { ...data, description: data.description || null };

      if (isEdit) {
        await updateConnector(connectorId, payload);
        enqueueSnackbar("Connector gespeichert", { variant: "success" });
      } else {
        await createConnector(payload);
        enqueueSnackbar("Connector angelegt", { variant: "success" });
      }

      onClose(true);
      return true;
    } catch (err) {
      const validation = err?.response?.data?.errors;
      if (validation) {
        Object.entries(validation).forEach(([field, messages]) => {
          setError(field, { message: messages[0] });
        });
      } else {
        enqueueSnackbar("Speichern fehlgeschlagen", { variant: "error" });
      }
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Connector bearbeiten" : "Neuer Connector"}
      </DialogTitle>

      {loading ? (
        <DialogContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <Form
          key={connectorId ?? "new"}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          resolver={yupResolver(schema)}
          mode="onChange"
        >
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <FormTextField name="name" label="Name" required fullWidth />

              <FormTextField
                name="slug"
                label="Slug"
                required
                fullWidth
                helperText="Eindeutiger technischer Bezeichner, z.B. 'openviva'"
                inputProps={{ style: { fontFamily: "monospace" } }}
              />

              <FormTextField
                name="description"
                label="Beschreibung"
                multiline
                minRows={2}
                fullWidth
              />

              <FormSwitch name="is_active" label="Aktiv" />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Box flexGrow={1} />
            <DsButton variant="tertiary" onClick={() => onClose(false)}>
              Abbrechen
            </DsButton>
            <SubmitButton requireDirty={false}>Speichern</SubmitButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
}

ImportConnectorFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  connectorId: PropTypes.number,
};

export default ImportConnectorFormModal;
